import { supabase } from "../supabaseClient";
import packageJson from "../../package.json";
import { getRuntimeDiagnostics } from "../utils/diagnosticsContext";

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const omitEmptyValues = (value) => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => omitEmptyValues(item))
      .filter((item) => item !== undefined);

    return items.length > 0 ? items : undefined;
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).reduce((acc, [key, nestedValue]) => {
      const normalizedValue = omitEmptyValues(nestedValue);

      if (normalizedValue !== undefined) {
        acc[key] = normalizedValue;
      }

      return acc;
    }, {});

    return Object.keys(entries).length > 0 ? entries : undefined;
  }

  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  return value;
};

const SENSITIVE_APP_CONTEXT_KEYS = new Set([
  "email",
  "user_email",
  "page_url",
  "user_agent",
  "userAgent",
  "vendor",
  "platform",
  "screen",
  "deviceMemory",
  "hardwareConcurrency",
  "referrer",
]);

const getSafePagePath = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.location?.pathname || null;
};

const buildSafeRuntimeContext = () => {
  const diagnostics = getRuntimeDiagnostics();

  return omitEmptyValues({
    online: diagnostics.online,
    visibilityState: diagnostics.visibilityState,
    standalone: diagnostics.standalone,
    viewport: diagnostics.viewport,
    connection: diagnostics.connection,
    timestamp: new Date().toISOString(),
  });
};

const sanitizeAppContext = (value) => {
  if (!isPlainObject(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(([key]) => !SENSITIVE_APP_CONTEXT_KEYS.has(key)),
  );
};

/**
 * Сервис для удаленного логирования ошибок в Supabase
 */
class ErrorLogService {
  /**
   * Сохранить ошибку в таблицу app_errors
   * @param {Object} errorData - Данные об ошибке
   */
  async logError({ message, error, context = {}, severity = "error" }) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const {
        error_meta: rawErrorMeta = {},
        runtime_context: rawRuntimeContext = {},
        app_context: rawAppContext = {},
        diagnostic_ids: rawDiagnosticIds = {},
        ...legacyContext
      } = context;

      const errorMeta = omitEmptyValues({
        name: error?.name,
        message: error?.message,
        code: error?.code,
        status: error?.status,
        details: error?.details,
        hint: error?.hint,
        ...rawErrorMeta,
      }) || {};

      const runtimeContext = omitEmptyValues({
        ...buildSafeRuntimeContext(),
        ...rawRuntimeContext,
      }) || {};

      const appContext = omitEmptyValues({
        ...sanitizeAppContext(legacyContext),
        ...sanitizeAppContext(rawAppContext),
      }) || {};

      const diagnosticIds = omitEmptyValues(rawDiagnosticIds) || {};

      const payload = {
        user_id: user?.id || null,
        user_email: null,
        page_url: getSafePagePath(),
        error_message: message || error?.message || "Unknown error",
        error_stack: error?.stack || null,
        context:
          omitEmptyValues({
            error_meta: errorMeta,
            runtime_context: runtimeContext,
            app_context: appContext,
            diagnostic_ids: diagnosticIds,
          }) || {},
        severity,
        version: packageJson.version,
      };

      // Используем insert без select, чтобы минимизировать нагрузку и избежать проблем с RLS
      const { error: dbError } = await supabase
        .from("app_errors")
        .insert([payload]);

      if (dbError) {
        // Silent failure to avoid recursive logging
      }
    } catch (err) {
      // Игнорируем ошибки самого логгера, чтобы не зациклиться
      // Silent failure
    }
  }
}

export const errorLogService = new ErrorLogService();
export default errorLogService;
